import { motion } from "framer-motion";
import { CalendarCheck, ShieldCheck, HeartPulse, Phone, ArrowRight } from "lucide-react";
import { DemoBar } from "@/components/DemoBar";
import { CASES } from "@/data/cases";

const img = CASES.find((c) => c.slug === "laege-support").image;

const SERVICES = [
  { icon: CalendarCheck, title: "Online tidsbestilling", text: "Book, flyt eller aflys din tid på under et minut — døgnet rundt." },
  { icon: HeartPulse, title: "Sundhedsrådgivning", text: "Skriftlige svar fra klinikken inden for 24 timer på hverdage." },
  { icon: ShieldCheck, title: "Tryg datahåndtering", text: "Alle oplysninger behandles krypteret og fortroligt." },
];

export default function LaegeSupportDemo() {
  return (
    <div data-testid="demo-laege-support" className="min-h-screen bg-[#F6FAFB] text-[#12302E]">
      <DemoBar name="LægeSupport" />

      <header className="flex items-center justify-between border-b border-[#12302E]/10 bg-white/70 px-6 py-5 backdrop-blur-md md:px-12">
        <p className="font-syne text-xl font-extrabold tracking-tight">
          Læge<span className="text-[#0E7C7B]">Support</span>
        </p>
        <button data-testid="laege-book-button" className="rounded-full bg-[#0E7C7B] px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105">
          Book tid
        </button>
      </header>

      <section className="grid grid-cols-1 items-center gap-12 px-6 py-20 md:grid-cols-2 md:px-12 md:py-28">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="text-sm uppercase tracking-[0.3em] text-[#0E7C7B]">Din klinik — altid ved hånden</p>
          <h1 className="mt-4 font-syne text-5xl font-extrabold leading-[0.95] tracking-tighter md:text-6xl">
            Tryg hjælp,<br />når du har brug for den
          </h1>
          <p className="mt-6 max-w-md text-[#12302E]/70">
            LægeSupport gør det nemt at være patient: bestil tid online, få svar på dine spørgsmål
            og mød altid et team, der kender dig.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="laege-hero-book-cta" className="group flex items-center gap-2 rounded-full bg-[#0E7C7B] px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105">
              Book en tid <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <span className="flex items-center gap-2 rounded-full border border-[#12302E]/15 bg-white px-6 py-4 text-sm text-[#12302E]/80">
              <Phone className="h-4 w-4 text-[#0E7C7B]" /> 70 12 34 56
            </span>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="overflow-hidden rounded-[2rem] shadow-2xl shadow-[#0E7C7B]/10"
        >
          <img src={img} alt="Læge i samtale med patient" data-testid="laege-hero-image" className="aspect-[4/3] w-full object-cover" />
        </motion.div>
      </section>

      <section className="bg-white px-6 py-20 md:px-12">
        <h2 className="font-syne text-3xl font-extrabold tracking-tighter md:text-5xl">Sådan hjælper vi</h2>
        <div data-testid="laege-services" className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="rounded-2xl border border-[#12302E]/10 bg-[#F6FAFB] p-8 transition-transform duration-300 hover:-translate-y-1"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0E7C7B]/10">
                  <Icon className="h-6 w-6 text-[#0E7C7B]" />
                </span>
                <h3 className="mt-5 font-syne text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#12302E]/70">{s.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-[#12302E]/10 px-6 py-10 text-center text-sm text-[#12302E]/60 md:px-12">
        LægeSupport · Sundhedsvej 3 · 8000 Aarhus C · kontakt@laegesupport.dk
      </footer>
    </div>
  );
}
